// src/authContext.ts
var userAuthType = Symbol("user");
var apiKeyAuthType = Symbol("api_key");
var AuthType = Object.freeze({
  User: userAuthType,
  ApiKey: apiKeyAuthType
});
var ApiKeyAuthContext = class {
  authType = AuthType.ApiKey;
  #account;
  #scopes;
  constructor(account, scopes) {
    this.#account = account;
    this.#scopes = new Set(scopes);
    Object.freeze(this.#account);
    Object.freeze(this.#scopes);
  }
  /**
   * Executes functions corresponding to the API key's scopes.
   *
   * Given a mapping of scope names to functions, this method
   * will execute each function whose scope matches the API key's permissions.
   *
   * @param scopeFnMap - A mapping of scopes to functions.
   *
   * @example
   *
   * const apiKeyContext = new ApiKeyAuthContext({ id: 1 }, ["read"]);
   *
   * apiKeyContext.executeForMatchingScopes({
   *   read: () => console.log('Has read scope'),
   *   write: () => console.log('Has write scope')
   * });
   *
   * // Outputs:
   * // Has read scope
   */
  executeForMatchingScopes(scopeFnMap) {
    for (const scope of this.#scopes) {
      const fn = scopeFnMap[scope];
      if (fn) {
        fn();
      }
    }
  }
  /**
   * The account associated with this API key.
   */
  get account() {
    return this.#account;
  }
  /**
   * A readonly set of permissions (like "read" or "write") granted to this API key.
   */
  get scopes() {
    return this.#scopes;
  }
};

// src/definePermissionsFor.ts
import {
  AbilityBuilder,
  buildMongoQueryMatcher,
  createAliasResolver,
  createMongoAbility
} from "@casl/ability";
import { $and, and } from "@ucast/mongo2js";

// src/operators.ts
import {
  FieldCondition
} from "@ucast/mongo2js";
var setIsSubset = (node, value, context) => {
  const fieldValue = context.get(value, node.field);
  const nodeValue = node.value;
  if (!(fieldValue instanceof Array)) {
    throw new Error(`$setIsSubset expects fieldValue (LHS) to be an array`);
  }
  return fieldValue.every((v) => nodeValue.includes(v));
};
var $setIsSubset = {
  type: "field",
  validate: (instruction, value) => {
    if (!Array.isArray(value)) {
      throw new Error(
        `$${instruction.name} expects nodeValue (RHS) to be an array`
      );
    }
  },
  parse(instruction, value, context) {
    return new FieldCondition(instruction.name, context.field, value);
  }
};

// src/subjects/account.ts
import { match } from "ts-pattern";

// src/helpers/rule.ts
var RuleHelper = class {
  constructor(authContext, builder) {
    this.authContext = authContext;
    this.builder = builder;
  }
  // Wrap a rule with an additional account id check.
  // This assumes that the PermissionsProvider function wrapping subject()
  // will provide the contextual account argument
  allowForAccount = (...[actions, subject2, conditions]) => {
    this.builder.allow(actions, subject2, {
      $and: [
        { "account.id": { $eq: this.authContext.account.id } },
        ...conditions ? [conditions] : []
      ]
    });
    this.builder.allow(actions, subject2, {
      $and: [
        { accountId: { $eq: this.authContext.account.id } },
        ...conditions ? [conditions] : []
      ]
    });
    this.builder.allow(actions, subject2, {
      $and: [
        { account_id: { $eq: this.authContext.account.id } },
        ...conditions ? [conditions] : []
      ]
    });
  };
};

// src/subjects/account.ts
var permissionsForAccount = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Account"),
      write: () => allowForAccount("manage", "Account")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Account");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "Account");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    () => {
      allowForAccount(
        [
          "view",
          "reset_account",
          "seed_account_with_demo_data",
          "copy_data_from_live",
          "try_with_demo_data",
          "sync_all"
        ],
        "Account"
      );
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount(
        [
          "view",
          "reset_account",
          "seed_account_with_demo_data",
          "copy_data_from_live",
          "try_with_demo_data",
          "sync_all"
        ],
        "Account"
      );
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "contributor" }
    },
    () => {
      allowForAccount("view", "Account");
    }
  ).exhaustive();
};

// src/subjects/actual.ts
import { P, match as match2 } from "ts-pattern";
var permissionsForActual = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match2(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Actual"),
      write: () => allowForAccount("manage", "Actual")
    });
  }).with(
    { authType: AuthType.User, account: { timesheets_protected: true } },
    (user) => {
      allowForAccount("crud", "Actual", {
        "person.email": { $eq: user.email.toLowerCase() }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Actual");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Actual");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: P._
      }
    },
    () => {
      allowForAccount("manage", "Actual");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: P._,
        manage_people: "all"
      }
    },
    () => {
      allowForAccount("manage", "Actual");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "none"
      }
    },
    (user) => {
      allowForAccount("crud", "Actual", {
        "person.email": { $eq: user.email.toLowerCase() }
      });
      allowForAccount(["create", "edit", "delete"], "Actual", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["create", "edit", "delete"], "Actual", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "none",
        manage_people: "restricted"
      }
    },
    (user) => {
      allowForAccount("crud", "Actual", {
        "person.email": { $eq: user.email.toLowerCase() }
      });
      allowForAccount(["create", "edit", "delete"], "Actual", {
        "person.id": {
          $in: user.manageable_people.map(({ person }) => person.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "restricted"
      }
    },
    (user) => {
      allowForAccount("crud", "Actual", {
        "person.email": { $eq: user.email.toLowerCase() }
      });
      allowForAccount(["create", "edit", "delete"], "Actual", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["create", "edit", "delete"], "Actual", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount(["create", "edit", "delete"], "Actual", {
        "person.id": {
          $in: user.manageable_people.map(({ person }) => person.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "none",
        manage_people: "none"
      }
    },
    (user) => {
      allowForAccount("crud", "Actual", {
        "person.email": { $eq: user.email.toLowerCase() }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "contributor" }
    },
    (user) => {
      allowForAccount("crud", "Actual", {
        "person.email": { $eq: user.email.toLowerCase() }
      });
    }
  ).exhaustive();
};

// src/subjects/all.ts
import { match as match3 } from "ts-pattern";
var permissionsForAll = (authContext, builder) => {
  const { allow } = builder;
  match3(authContext).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "all");
    }
  ).otherwise(() => {
  });
};

// src/subjects/api.ts
import { P as P2, match as match4 } from "ts-pattern";
var permissionsForAPI = (authContext, builder) => {
  const { allow } = builder;
  const { allowForAccount } = new RuleHelper(authContext, builder);
  match4(authContext).with({ authType: AuthType.ApiKey, scopes: P2.select() }, () => {
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "API");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "API");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    () => {
      allowForAccount("manage", "API");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/assignment.ts
import { match as match5 } from "ts-pattern";
var permissionsForAssignment = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match5(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Assignment"),
      write: () => allowForAccount("manage", "Assignment")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Assignment");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Assignment");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "all" }
    },
    () => {
      allowForAccount("crud", "Assignment");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "restricted" }
    },
    (user) => {
      allowForAccount("crud", "Assignment", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount("crud", "Assignment", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("crud", "Assignment", {
        "project.isTemplate": true
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "none" }
    },
    () => {
      allowForAccount("view", "Assignment");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "contributor" }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/client.ts
import { match as match6 } from "ts-pattern";
var permissionsForClient = (user, builder) => {
  const { allowForAccount } = new RuleHelper(user, builder);
  const { allow } = builder;
  match6(user).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Client"),
      write: () => allowForAccount("manage", "Client")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Client");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Client");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager"
      }
    },
    () => {
      allowForAccount("view", "Client");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/competency.ts
import { match as match7 } from "ts-pattern";
var permissionsForCompetency = (user, builder) => {
  const { allowForAccount } = new RuleHelper(user, builder);
  const { allow } = builder;
  match7(user).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Competency"),
      write: () => allowForAccount("manage", "Competency")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Competency");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Competency");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "Competency");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/contract.ts
import { match as match8 } from "ts-pattern";
var permissionsForContract = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match8(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Contract"),
      write: () => allowForAccount("manage", "Contract")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Contract");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "admin"
      }
    },
    () => {
      allowForAccount("manage", "Contract");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_people: "all"
      }
    },
    () => {
      allowForAccount("manage", "Contract");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_people: "restricted"
      }
    },
    (user) => {
      allowForAccount("view", "Contract");
      allowForAccount("manage", "Contract", {
        "person.id": {
          $in: user.manageable_people.map(({ person }) => person.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager"
      }
    },
    () => {
      allowForAccount("view", "Contract");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/customField.ts
import { match as match9 } from "ts-pattern";
var permissionsForCustomField = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match9(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "CustomField"),
      write: () => allowForAccount("manage", "CustomField")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "CustomField");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "CustomField");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/export.ts
import { match as match10 } from "ts-pattern";
var permissionsForExport = (user, builder) => {
  const { allowForAccount } = new RuleHelper(user, builder);
  const { allow } = builder;
  match10(user).with({ authType: AuthType.ApiKey }, () => {
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow(["view", "create"], "Export");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", financial: "all" }
    },
    () => {
      allowForAccount(["view", "create"], "Export");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", financial: "all" }
    },
    () => {
      allowForAccount(["view", "create"], "Export");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/exportReports.ts
import { P as P3, match as match11 } from "ts-pattern";
var permissionsForExportReports = (authContext, builder) => {
  switch (authContext.authType) {
    case AuthType.User: {
      match11(authContext.permissions.type).with("superuser", () => {
        builder.allow("view", "ExportReports");
      }).with(P3.union("admin", "manager"), () => {
        const rule = new RuleHelper(authContext, builder);
        rule.allowForAccount("view", "ExportReports");
      }).otherwise(() => {
      });
    }
  }
};

// src/subjects/feature.ts
import { match as match12 } from "ts-pattern";
var permissionsForFeature = (authContext, builder) => {
  const { allow } = builder;
  match12(authContext).with({ authType: AuthType.ApiKey }, () => {
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Feature");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/financial.ts
import { P as P4, match as match13 } from "ts-pattern";
var permissionsForFinancial = (authContext, builder) => {
  const { allow } = builder;
  const { allowForAccount } = new RuleHelper(authContext, builder);
  match13(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Financial"),
      write: () => allowForAccount("view", "Financial")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("view", "Financial");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { financial: P4.union("all", "restricted") }
    },
    () => {
      allowForAccount("view", "Financial");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { financial: "none" }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/helpDocument.ts
import { match as match14 } from "ts-pattern";
var permissionsForHelpDocument = (authContext, builder) => {
  const { allow } = builder;
  match14(authContext).with({ authType: AuthType.ApiKey }, () => {
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("view", "HelpDocument");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allow("view", "HelpDocument");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allow("view", "HelpDocument");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
      allow("view", "HelpDocument");
    }
  ).exhaustive();
};

// src/subjects/holiday.ts
import { match as match15 } from "ts-pattern";
var permissionsForHoliday = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match15(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Holiday"),
      write: () => allowForAccount("manage", "Holiday")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Holiday");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Holiday");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "Holiday");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/holidaysGroup.ts
import { match as match16 } from "ts-pattern";
var permissionsForHolidaysGroup = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match16(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "HolidaysGroup"),
      write: () => allowForAccount("manage", "HolidaysGroup")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "HolidaysGroup");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "HolidaysGroup");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/import.ts
import { match as match17 } from "ts-pattern";
var permissionsForImport = (user, builder) => {
  const { allowForAccount } = new RuleHelper(user, builder);
  const { allow } = builder;
  match17(user).with({ authType: AuthType.ApiKey }, () => {
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow(["view", "create"], "Import");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount(["view", "create"], "Import");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    (_user) => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/importExport.ts
import { match as match18 } from "ts-pattern";
var permissionsForImportExport = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match18(authContext).with({ authType: AuthType.ApiKey }, () => {
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "ImportExport");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "ImportExport");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/insights.ts
import { match as match19 } from "ts-pattern";
var permissionsForInsights = (authContext, builder) => {
  const { allow } = builder;
  match19(authContext).with({ authType: AuthType.ApiKey }, () => {
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allow("view", "Insights");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "all"
      }
    },
    () => {
      allow("view", "Insights");
    }
  ).with(
    {
      authType: AuthType.User
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/integration.ts
import { match as match20 } from "ts-pattern";
var permissionsForIntegration = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match20(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Integration"),
      write: () => allowForAccount("manage", "Integration")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Integration");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "Integration");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/invitation.ts
import { match as match21 } from "ts-pattern";
var permissionsForInvitation = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match21(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Invitation"),
      write: () => allowForAccount("manage", "Invitation")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Invitation");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "Invitation");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    () => {
      allowForAccount("view", "Invitation");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/loggedAction.ts
import { match as match22 } from "ts-pattern";
var permissionsForLoggedAction = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match22(authContext).with({ authType: AuthType.ApiKey }, () => {
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("view", "LoggedAction");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("view", "LoggedAction");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "LoggedAction");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/milestone.ts
import { match as match23 } from "ts-pattern";
var permissionsForMilestone = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match23(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Milestone"),
      write: () => allowForAccount("manage", "Milestone")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Milestone");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Milestone");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "all" }
    },
    () => {
      allowForAccount("crud", "Milestone");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted"
      }
    },
    (user) => {
      allowForAccount("crud", "Milestone", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount("crud", "Milestone", {
        "project.isTemplate": true
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "none" }
    },
    () => {
      allowForAccount("view", "Milestone");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/peopleManager.ts
import { match as match24 } from "ts-pattern";
var permissionsForPeopleManager = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match24(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "PeopleManager"),
      write: () => allowForAccount("manage", "PeopleManager")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "PeopleManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "PeopleManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("view", "PeopleManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "PeopleManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/person.ts
import { match as match25 } from "ts-pattern";
var permissionsForPerson = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match25(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Person"),
      write: () => allowForAccount("manage", "Person")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Person");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Person");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_people: "all" }
    },
    () => {
      allowForAccount("crud", "Person");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_people: "restricted" }
    },
    (user) => {
      allowForAccount(["create", "view"], "Person");
      allowForAccount(["edit", "delete"], "Person", {
        id: {
          $in: user.manageable_people.map(({ person }) => person.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_people: "none" }
    },
    () => {
      allowForAccount("view", "Person");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/personNote.ts
import { match as match26 } from "ts-pattern";
var permissionsForPersonNote = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match26(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => {
        allowForAccount("view", "PersonNote");
      },
      write: () => {
        allowForAccount(["view", "create", "delete"], "PersonNote");
      }
    });
  }).with(
    { authType: AuthType.User, permissions: { type: "superuser" } },
    () => {
      allow(["view", "delete"], "PersonNote");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    (user) => {
      allowForAccount(["view", "delete", "create"], "PersonNote");
      allowForAccount("edit", "PersonNote", {
        "user.id": { $eq: user.id }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_people: "all" }
    },
    (user) => {
      allowForAccount(["view", "create"], "PersonNote");
      allowForAccount(["edit", "delete"], "PersonNote", {
        "user.id": { $eq: user.id }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_people: "restricted" }
    },
    (user) => {
      allowForAccount("view", "PersonNote");
      allowForAccount("create", "PersonNote", {
        "person.id": {
          $in: user.manageable_people.map(({ person }) => person.id)
        }
      });
      allowForAccount("create", "PersonNote", {
        "person.is_placeholder": true
      });
      allowForAccount(["edit", "delete"], "PersonNote", {
        "user.id": { $eq: user.id }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_people: "none" }
    },
    () => {
      allowForAccount("view", "PersonNote");
      allowForAccount("create", "PersonNote", {
        "person.is_placeholder": true
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "contributor" }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/personRequest.ts
import { match as match27 } from "ts-pattern";
var permissionsForPersonRequest = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match27(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "PersonRequest"),
      write: () => allowForAccount("manage", "PersonRequest")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "PersonRequest");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "PersonRequest");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "all" }
    },
    () => {
      allowForAccount("crud", "PersonRequest");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted"
      }
    },
    (user) => {
      allowForAccount("view", "PersonRequest");
      allowForAccount(["edit", "create", "delete"], "PersonRequest", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["edit", "create", "delete"], "PersonRequest", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("manage", "PersonRequest", {
        "project.isTemplate": true
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "none" }
    },
    () => {
      allowForAccount("view", "PersonRequest");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/phase.ts
import { match as match28 } from "ts-pattern";
var permissionsForPhase = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match28(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Phase"),
      write: () => allowForAccount("manage", "Phase")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Phase");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Phase");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "all" }
    },
    () => {
      allowForAccount("crud", "Phase");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted"
      }
    },
    (user) => {
      allowForAccount("crud", "Phase", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount("crud", "Phase", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("crud", "Phase", {
        "project.isTemplate": true
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "none" }
    },
    () => {
      allowForAccount("view", "Phase");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/placeholder.ts
import { match as match29 } from "ts-pattern";
var permissionsForPlaceholder = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match29(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Placeholder"),
      write: () => allowForAccount("manage", "Placeholder")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Placeholder");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Placeholder");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "all" }
    },
    () => {
      allowForAccount("crud", "Placeholder");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted"
      }
    },
    (user) => {
      allowForAccount("view", "Placeholder");
      allowForAccount(["edit", "create", "delete"], "Placeholder", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["edit", "create", "delete"], "Placeholder", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("manage", "Placeholder", {
        "project.isTemplate": true
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "none" }
    },
    () => {
      allowForAccount("view", "Placeholder");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/planner.ts
import { match as match30 } from "ts-pattern";
var permissionsForPlanner = (authContext, builder) => {
  const { allow } = builder;
  match30(authContext).with({ authType: AuthType.ApiKey }, () => {
  }).with(
    {
      authType: AuthType.User,
      permissions: { view_planner: true }
    },
    () => {
      allow("view", "Planner");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { view_planner: false }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/project.ts
import { match as match31 } from "ts-pattern";
var permissionsForProject = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match31(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Project"),
      write: () => allowForAccount("manage", "Project")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Project");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Project");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "all" }
    },
    () => {
      allowForAccount("crud", "Project");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted"
      }
    },
    (user) => {
      allowForAccount(["create", "view"], "Project");
      allowForAccount(["edit", "delete"], "Project", {
        id: {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["edit", "delete"], "Project", {
        teamId: {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "none" }
    },
    () => {
      allowForAccount("view", "Project");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/projectBudget.ts
import { match as match32 } from "ts-pattern";
var permissionsForProjectBudget = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match32(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "ProjectBudget"),
      write: () => allowForAccount("manage", "ProjectBudget")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "ProjectBudget");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", financial: "all" }
    },
    () => {
      allowForAccount("manage", "ProjectBudget");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "all",
        manage_projects: "all"
      }
    },
    () => {
      allowForAccount("crud", "ProjectBudget");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "all",
        manage_projects: "restricted"
      }
    },
    (user) => {
      allowForAccount("view", "ProjectBudget");
      allowForAccount(["create", "edit", "delete"], "ProjectBudget", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["create", "edit", "delete"], "ProjectBudget", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("manage", "ProjectBudget", {
        "project.isTemplate": true
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "all",
        manage_projects: "none"
      }
    },
    () => {
      allowForAccount("view", "ProjectBudget");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "restricted"
      }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "contributor" }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/projectManager.ts
import { match as match33 } from "ts-pattern";
var permissionsForProjectManager = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match33(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "ProjectManager"),
      write: () => allowForAccount("manage", "ProjectManager")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "ProjectManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "ProjectManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("view", "ProjectManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "ProjectManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/projectMember.ts
import { match as match34 } from "ts-pattern";
var permissionsForProjectMember = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match34(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "ProjectMember"),
      write: () => allowForAccount("manage", "ProjectMember")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "superuser"
      }
    },
    () => {
      allow("manage", "ProjectMember");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "admin"
      }
    },
    () => {
      allowForAccount("manage", "ProjectMember");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "all",
        add_all_people_to_projects: true
      }
    },
    () => {
      allowForAccount("manage", "ProjectMember");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "all",
        add_all_people_to_projects: false
      }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "restricted",
        add_all_people_to_projects: true
      }
    },
    () => {
      allowForAccount("manage", "ProjectMember");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "restricted",
        add_all_people_to_projects: false
      }
    },
    (user) => {
      allowForAccount(["view", "edit", "delete"], "ProjectMember");
      allowForAccount(["create"], "ProjectMember", {
        "person.id": {
          $in: user.manageable_people.map(({ person }) => person.id)
        }
      });
      allowForAccount("create", "ProjectMember", {
        isProjectMember: {
          $eq: true
        }
      });
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            "person.id": {
              $eq: user.person?.id
            }
          },
          { location: { $ne: "planner" } }
        ]
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "all"
      }
    },
    (user) => {
      allowForAccount(["view", "create", "delete"], "ProjectMember");
      allowForAccount("edit", "ProjectMember", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount("edit", "ProjectMember", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            "person.id": {
              $eq: user.person?.id
            }
          },
          { location: { $ne: "planner" } }
        ]
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "restricted",
        add_all_people_to_projects: true
      }
    },
    (user) => {
      allowForAccount("view", "ProjectMember");
      allowForAccount(["create", "delete"], "ProjectMember", {
        "person.id": {
          $in: user.manageable_people.map(({ person }) => person.id)
        }
      });
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            "person.id": {
              $eq: user.person?.id
            }
          },
          { location: { $ne: "planner" } }
        ]
      });
      allowForAccount(["create", "edit", "delete"], "ProjectMember", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["create", "edit", "delete"], "ProjectMember", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "restricted",
        add_all_people_to_projects: false
      }
    },
    (user) => {
      allowForAccount("view", "ProjectMember");
      allowForAccount(["create", "delete"], "ProjectMember", {
        "person.id": {
          $in: user.manageable_people.map(({ person }) => person.id)
        }
      });
      allowForAccount(["edit", "delete"], "ProjectMember", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["edit", "delete"], "ProjectMember", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            isProjectMember: {
              $eq: true
            }
          },
          {
            "project.id": {
              $in: user.manageable_projects.map(({ project }) => project.id)
            }
          }
        ]
      });
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            "person.id": {
              $eq: user.person?.id
            }
          },
          { location: { $ne: "planner" } }
        ]
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "none",
        add_all_people_to_projects: true
      }
    },
    () => {
      allowForAccount("manage", "ProjectMember");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "none",
        add_all_people_to_projects: false
      }
    },
    () => {
      allowForAccount(["view", "edit", "delete"], "ProjectMember");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "none",
        add_all_people_to_projects: true
      }
    },
    (user) => {
      allowForAccount("view", "ProjectMember");
      allowForAccount(["create", "edit", "delete"], "ProjectMember", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["create", "edit", "delete"], "ProjectMember", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            "person.id": {
              $eq: user.person?.id
            }
          },
          { location: { $ne: "planner" } }
        ]
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "none",
        add_all_people_to_projects: false
      }
    },
    (user) => {
      allowForAccount("view", "ProjectMember");
      allowForAccount(["edit", "delete"], "ProjectMember", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["edit", "delete"], "ProjectMember", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            isProjectMember: {
              $eq: true
            }
          },
          {
            "project.id": {
              $in: user.manageable_projects.map(({ project }) => project.id)
            }
          }
        ]
      });
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            "person.id": {
              $eq: user.person?.id
            }
          },
          { location: { $ne: "planner" } }
        ]
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "none",
        manage_people: "all"
      }
    },
    () => {
      allowForAccount(["view", "create", "delete"], "ProjectMember");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "none",
        manage_people: "restricted"
      }
    },
    (user) => {
      allowForAccount("view", "ProjectMember");
      allowForAccount(["create", "delete"], "ProjectMember", {
        "person.id": {
          $in: user.manageable_people.map(({ person }) => person.id)
        }
      });
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            "person.id": {
              $eq: user.person?.id
            }
          },
          { location: { $ne: "planner" } }
        ]
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "none",
        manage_people: "none"
      }
    },
    (user) => {
      allowForAccount("view", "ProjectMember");
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            "person.id": {
              $eq: user.person?.id
            }
          },
          { location: { $ne: "planner" } }
        ]
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    (user) => {
      allowForAccount("view", "ProjectMember");
      allowForAccount("create", "ProjectMember", {
        $and: [
          {
            "person.id": {
              $eq: user.person?.id
            }
          },
          { location: { $ne: "planner" } }
        ]
      });
    }
  ).exhaustive();
};

// src/subjects/projectNote.ts
import { match as match35 } from "ts-pattern";
var permissionsForProjectNote = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  match35(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "ProjectNote"),
      write: () => allowForAccount(["view", "create", "delete"], "ProjectNote")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    (user) => {
      allowForAccount(["view", "delete"], "ProjectNote");
      allowForAccount(["create", "edit"], "ProjectNote", {
        "user.id": { $eq: user.id }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    (user) => {
      allowForAccount(["view", "delete"], "ProjectNote");
      allowForAccount(["create", "edit"], "ProjectNote", {
        "user.id": { $eq: user.id }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "all" }
    },
    (user) => {
      allowForAccount(["view", "create"], "ProjectNote");
      allowForAccount(["edit", "delete"], "ProjectNote", {
        "user.id": { $eq: user.id }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted"
      }
    },
    (user) => {
      allowForAccount("view", "ProjectNote");
      allowForAccount("create", "ProjectNote", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount("create", "ProjectNote", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
      allowForAccount("create", "ProjectNote", {
        "project.isTemplate": true
      });
      allowForAccount(["edit", "delete"], "ProjectNote", {
        "user.id": { $eq: user.id }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "none" }
    },
    () => {
      allowForAccount("view", "ProjectNote");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/projectRate.ts
import { match as match36 } from "ts-pattern";
var permissionsForProjectRate = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match36(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "ProjectRate"),
      write: () => allowForAccount("manage", "ProjectRate")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "ProjectRate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", financial: "all" }
    },
    () => {
      allowForAccount("manage", "ProjectRate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "all",
        manage_projects: "all"
      }
    },
    () => {
      allowForAccount("crud", "ProjectRate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "all",
        manage_projects: "restricted"
      }
    },
    (user) => {
      allowForAccount(["view"], "ProjectRate");
      allowForAccount(["create", "edit", "delete"], "ProjectRate", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["create", "edit", "delete"], "ProjectRate", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "all",
        manage_projects: "none"
      }
    },
    () => {
      allowForAccount("view", "ProjectRate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "restricted"
      }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/projectTeamManager.ts
import { match as match37 } from "ts-pattern";
var permissionsForProjectTeamManager = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match37(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "ProjectTeamManager"),
      write: () => allowForAccount("manage", "ProjectTeamManager")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "ProjectTeamManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "ProjectTeamManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("view", "ProjectTeamManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "ProjectTeamManager");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/projectTemplate.ts
import { match as match38 } from "ts-pattern";
var permissionsForProjectTemplate = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match38(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "ProjectTemplate"),
      write: () => allowForAccount("manage", "ProjectTemplate")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "ProjectTemplate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "ProjectTemplate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "all"
      }
    },
    () => {
      allowForAccount("manage", "ProjectTemplate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "restricted"
      }
    },
    (user) => {
      allowForAccount("crud", "ProjectTemplate");
      allowForAccount("create-from-template", "ProjectTemplate", {
        members: {
          $setIsSubset: user.manageable_people.map(({ person }) => person.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "none"
      }
    },
    () => {
      allowForAccount("crud", "ProjectTemplate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "all"
      }
    },
    () => {
      allowForAccount("manage", "ProjectTemplate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "restricted"
      }
    },
    (user) => {
      allowForAccount("crud", "ProjectTemplate");
      allowForAccount("create-from-template", "ProjectTemplate", {
        members: {
          $setIsSubset: user.manageable_people.map(({ person }) => person.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted",
        manage_people: "none"
      }
    },
    (user) => {
      allowForAccount("crud", "ProjectTemplate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "none" }
    },
    () => {
      allowForAccount("view", "ProjectTemplate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/projectWorkstream.ts
import { match as match39 } from "ts-pattern";
var permissionsForProjectWorkstream = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match39(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "ProjectWorkstream"),
      write: () => allowForAccount("manage", "ProjectWorkstream")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "ProjectWorkstream");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "ProjectWorkstream");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "all" }
    },
    () => {
      allowForAccount("crud", "ProjectWorkstream");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "restricted"
      }
    },
    (user) => {
      allowForAccount(["create", "view"], "ProjectWorkstream");
      allowForAccount(["edit", "delete"], "ProjectWorkstream", {
        "project.id": {
          $in: user.manageable_projects.map(({ project }) => project.id)
        }
      });
      allowForAccount(["edit", "delete"], "ProjectWorkstream", {
        "project.teamId": {
          $in: user.manageable_project_teams.map(({ team }) => team.id)
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_projects: "none" }
    },
    () => {
      allowForAccount("view", "ProjectWorkstream");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/rateCard.ts
import { match as match40 } from "ts-pattern";
var permissionsForRateCard = (user, builder) => {
  const { allowForAccount } = new RuleHelper(user, builder);
  const { allow } = builder;
  match40(user).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "RateCard"),
      write: () => allowForAccount("manage", "RateCard")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "RateCard");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", financial: "all" }
    },
    () => {
      allowForAccount("manage", "RateCard");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", financial: "restricted" }
    },
    () => {
      allowForAccount("view", "RateCard");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", financial: "none" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "all"
      }
    },
    () => {
      allowForAccount("view", "RateCard");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", financial: "restricted" }
    },
    () => {
      allowForAccount("view", "RateCard");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", financial: "none" }
    },
    () => {
    }
  ).with(
    { authType: AuthType.User, permissions: { type: "contributor" } },
    () => {
    }
  ).exhaustive();
};

// src/subjects/report.ts
import { match as match41 } from "ts-pattern";
var permissionsForReport = (user, builder) => {
  const { allowForAccount } = new RuleHelper(user, builder);
  const { allow } = builder;
  match41(user).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Report"),
      write: () => allowForAccount("view", "Report")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("view", "Report");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("view", "Report");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "Report");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/role.ts
import { match as match42 } from "ts-pattern";
var permissionsForRole = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match42(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Role"),
      write: () => allowForAccount("manage", "Role")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Role");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Role");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "Role");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/roleChargeOutRate.ts
import { match as match43 } from "ts-pattern";
var permissionsForRoleChargeOutRate = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match43(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "RoleChargeOutRate"),
      write: () => allowForAccount("manage", "RoleChargeOutRate")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "RoleChargeOutRate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", financial: "all" }
    },
    () => {
      allowForAccount("manage", "RoleChargeOutRate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        financial: "all"
      }
    },
    () => {
      allowForAccount("view", "RoleChargeOutRate");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/salary.ts
import { P as P5, match as match44 } from "ts-pattern";
var permissionsForSalary = (authContext, builder) => {
  const { allow } = builder;
  const { allowForAccount } = new RuleHelper(authContext, builder);
  match44(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Salary"),
      write: () => allowForAccount("manage", "Salary")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("view", "Salary");
    }
  ).with(
    { authType: AuthType.User, permissions: { financial: "all" } },
    () => {
      allowForAccount("view", "Salary");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { financial: P5.union("restricted", "none") }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/savedInsightsDashboard.ts
import { match as match45 } from "ts-pattern";
var permissionsForSavedInsightsDashboard = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match45(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "SavedInsightsDashboard"),
      write: () => allowForAccount("manage", "SavedInsightsDashboard")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "SavedInsightsDashboard");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    (user) => {
      allowForAccount("manage", "SavedInsightsDashboard", {
        creatorId: {
          $eq: user.id
        }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "all",
        manage_people: "all"
      }
    },
    (user) => {
      allowForAccount("manage", "SavedInsightsDashboard", {
        creatorId: {
          $eq: user.id
        }
      });
    }
  ).with(
    {
      authType: AuthType.User
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/savedReport.ts
import { P as P6, match as match46 } from "ts-pattern";
var permissionsForSavedReport = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match46(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "SavedReport"),
      write: () => allowForAccount("manage", "SavedReport")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "SavedReport");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "SavedReport");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: P6.union("all", "restricted")
      }
    },
    () => {
      allowForAccount("crud", "SavedReport");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "none",
        manage_people: P6.union("all", "restricted")
      }
    },
    () => {
      allowForAccount("crud", "SavedReport");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "manager",
        manage_projects: "none",
        manage_people: "none"
      }
    },
    () => {
      allowForAccount("view", "SavedReport");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/skill.ts
import { match as match47 } from "ts-pattern";
var permissionsForSkill = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match47(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Skill"),
      write: () => allowForAccount("manage", "Skill")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Skill");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Skill");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "Skill");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/tag.ts
import { match as match48 } from "ts-pattern";
var permissionsForTag = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match48(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Tag"),
      write: () => allowForAccount("manage", "Tag")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Tag");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Tag");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "Tag");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/team.ts
import { match as match49 } from "ts-pattern";
var permissionsForTeam = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match49(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Team"),
      write: () => allowForAccount("manage", "Team")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Team");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Team");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "Team");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/timeOff.ts
import { match as match50 } from "ts-pattern";
var permissionsForTimeOff = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match50(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "TimeOff"),
      write: () => allowForAccount("manage", "TimeOff")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "TimeOff");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "TimeOff");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_people: "all" }
    },
    () => {
      allowForAccount("manage", "TimeOff");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_people: "restricted" }
    },
    (user) => {
      if (user.manageable_people.length > 0) {
        allowForAccount("crud", "TimeOff", {
          "person.id": {
            $in: user.manageable_people.map(({ person }) => person.id)
          }
        });
      }
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager", manage_people: "none" }
    },
    () => {
      allowForAccount("view", "TimeOff");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/user.ts
import { match as match51 } from "ts-pattern";
var permissionsForUser = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match51(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "User"),
      write: () => allowForAccount(["edit", "view", "delete"], "User")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "User");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount(["edit", "view", "delete"], "User");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    (user) => {
      allowForAccount(["edit", "view", "delete"], "User", {
        id: { $eq: user.id }
      });
      allowForAccount("view", "User");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    (user) => {
      allowForAccount(["edit", "view", "delete"], "User", {
        id: { $eq: user.id }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    (user) => {
      allowForAccount(["edit", "view", "delete"], "User", {
        id: { $eq: user.id }
      });
    }
  ).exhaustive();
};

// src/subjects/userFilterSet.ts
import { match as match52 } from "ts-pattern";
var permissionsForUserFilterSet = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match52(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "UserFilterSet"),
      write: () => allowForAccount("manage", "UserFilterSet")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "UserFilterSet");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "UserFilterSet");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("crud", "UserFilterSet");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor",
        view_planner: true
      }
    },
    () => {
      allowForAccount("crud", "UserFilterSet");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor",
        view_planner: false
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/userPermission.ts
import { match as match53 } from "ts-pattern";
var permissionsForUserPermission = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match53(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "UserPermission"),
      write: () => allowForAccount("manage", "UserPermission")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "UserPermission");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    (user) => {
      allowForAccount("manage", "UserPermission", {
        id: { $ne: user.id }
      });
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    () => {
      allowForAccount("view", "UserPermission");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "UserPermission");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/view.ts
import { match as match54 } from "ts-pattern";
var permissionsForView = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match54(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "View"),
      write: () => allowForAccount("manage", "View")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "View");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: true }
    },
    () => {
      allowForAccount("manage", "View");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin", manage_account: false }
    },
    () => {
      allowForAccount("view", "View");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "View");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor",
        view_planner: true
      }
    },
    () => {
      allowForAccount("view", "View");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor",
        view_planner: false
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/workstream.ts
import { match as match55 } from "ts-pattern";
var permissionsForWorkstream = (authContext, builder) => {
  const { allowForAccount } = new RuleHelper(authContext, builder);
  const { allow } = builder;
  match55(authContext).with({ authType: AuthType.ApiKey }, (apiKey) => {
    apiKey.executeForMatchingScopes({
      read: () => allowForAccount("view", "Workstream"),
      write: () => allowForAccount("manage", "Workstream")
    });
  }).with(
    {
      authType: AuthType.User,
      permissions: { type: "superuser" }
    },
    () => {
      allow("manage", "Workstream");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "admin" }
    },
    () => {
      allowForAccount("manage", "Workstream");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: { type: "manager" }
    },
    () => {
      allowForAccount("view", "Workstream");
    }
  ).with(
    {
      authType: AuthType.User,
      permissions: {
        type: "contributor"
      }
    },
    () => {
    }
  ).exhaustive();
};

// src/subjects/index.ts
var buildPermissions = (authContext, builder) => {
  permissionsForAll(authContext, builder);
  permissionsForAccount(authContext, builder);
  permissionsForPlanner(authContext, builder);
  permissionsForProject(authContext, builder);
  permissionsForAPI(authContext, builder);
  permissionsForActual(authContext, builder);
  permissionsForAssignment(authContext, builder);
  permissionsForExport(authContext, builder);
  permissionsForClient(authContext, builder);
  permissionsForContract(authContext, builder);
  permissionsForCustomField(authContext, builder);
  permissionsForExportReports(authContext, builder);
  permissionsForFeature(authContext, builder);
  permissionsForHelpDocument(authContext, builder);
  permissionsForHolidaysGroup(authContext, builder);
  permissionsForImport(authContext, builder);
  permissionsForImportExport(authContext, builder);
  permissionsForInvitation(authContext, builder);
  permissionsForFinancial(authContext, builder);
  permissionsForUserPermission(authContext, builder);
  permissionsForPerson(authContext, builder);
  permissionsForRateCard(authContext, builder);
  permissionsForReport(authContext, builder);
  permissionsForRole(authContext, builder);
  permissionsForRoleChargeOutRate(authContext, builder);
  permissionsForSalary(authContext, builder);
  permissionsForSavedReport(authContext, builder);
  permissionsForSkill(authContext, builder);
  permissionsForTag(authContext, builder);
  permissionsForTeam(authContext, builder);
  permissionsForTimeOff(authContext, builder);
  permissionsForUser(authContext, builder);
  permissionsForView(authContext, builder);
  permissionsForProjectTemplate(authContext, builder);
  permissionsForProjectBudget(authContext, builder);
  permissionsForIntegration(authContext, builder);
  permissionsForLoggedAction(authContext, builder);
  permissionsForMilestone(authContext, builder);
  permissionsForProjectRate(authContext, builder);
  permissionsForProjectNote(authContext, builder);
  permissionsForPhase(authContext, builder);
  permissionsForPlaceholder(authContext, builder);
  permissionsForHoliday(authContext, builder);
  permissionsForPersonRequest(authContext, builder);
  permissionsForUserFilterSet(authContext, builder);
  permissionsForCompetency(authContext, builder);
  permissionsForPersonNote(authContext, builder);
  permissionsForProjectMember(authContext, builder);
  permissionsForWorkstream(authContext, builder);
  permissionsForProjectWorkstream(authContext, builder);
  permissionsForPeopleManager(authContext, builder);
  permissionsForProjectManager(authContext, builder);
  permissionsForProjectTeamManager(authContext, builder);
  permissionsForInsights(authContext, builder);
  permissionsForSavedInsightsDashboard(authContext, builder);
};
var subjects_default = buildPermissions;

// src/definePermissionsFor.ts
var definePermissionsFor = (authContext) => {
  const resolveAction = createAliasResolver({
    crud: ["create", "view", "edit", "delete"]
  });
  const createAppAbility = createMongoAbility;
  const builder = new AbilityBuilder(createAppAbility);
  const maskedBuilder = {
    ...builder,
    allow: builder.can
  };
  const conditionsMatcher = buildMongoQueryMatcher(
    { $and, $setIsSubset },
    { and, setIsSubset }
  );
  subjects_default(authContext, maskedBuilder);
  return maskedBuilder.build({
    detectSubjectType: (object) => object.__caslSubjectType__,
    conditionsMatcher,
    resolveAction
  });
};

// src/helpers/subject.ts
var subject = (name, obj) => {
  const proxy = new Proxy(obj, {
    get(target, prop) {
      if (prop === "__caslSubjectType__") {
        return name;
      }
      return Reflect.get(target, prop);
    }
  });
  return proxy;
};
export {
  ApiKeyAuthContext,
  AuthType,
  definePermissionsFor,
  subject
};
//# sourceMappingURL=index.js.map